import React, { ComponentProps, ReactElement, ReactNode, Ref } from 'react';

import ControlLabel from './ControlLabel';
import Placeholder from './Placeholder';
import Selected from './Selected';
import { defaultLabelGetter } from '../../helpers/defaultGetters';
import { GenericItem, GetToggleButtonProps, ItemToString } from '../../types';

export type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof ControlLabel
> &
  GetToggleButtonProps & {
    isLoading?: boolean;
    selectedItem: TItem | null;
    itemToString?: ItemToString<TItem>;
    placeholder?: string;
    renderInput?: () => ReactNode;
    renderPlaceholder?: (
      props?: ComponentProps<typeof Placeholder>,
    ) => ReactNode;
    renderItem?: ({
      selectedItem,
      itemToString,
      isOpen,
    }: {
      selectedItem?: TItem | null;
      itemToString: ItemToString<TItem>;
      isOpen: boolean;
    }) => ReactNode;
    highDensity?: boolean;
    highlightButtonOnFocus?: boolean;
  };

const ControlWrapped = <TItem extends GenericItem>(
  {
    isOpen = false,
    selectedItem = null,
    renderInput,
    itemToString = defaultLabelGetter,
    renderPlaceholder = (placeholderProps) => (
      <Placeholder {...placeholderProps} />
    ),
    renderItem = () => <Selected>{itemToString(selectedItem)}</Selected>,
    getToggleButtonProps,
    placeholder,
    highDensity,
    highlightButtonOnFocus,
    ...props
  }: ControlProps<TItem>,
  ref: Ref<HTMLDivElement | HTMLLabelElement>,
) => (
  <ControlLabel
    ref={ref}
    isOpen={isOpen}
    getToggleButtonProps={getToggleButtonProps}
    highDensity={highDensity}
    highlightButtonOnFocus={highlightButtonOnFocus}
    {...props}
  >
    {renderInput && renderInput()}
    {selectedItem && renderItem({ selectedItem, itemToString, isOpen })}
    {!selectedItem && renderPlaceholder({ children: placeholder })}
  </ControlLabel>
);

const Control = React.forwardRef(ControlWrapped);

Control.displayName = 'Control';

Control.propTypes = {
  ...ControlLabel.propTypes,
};

export default Control as <T extends GenericItem>(
  p: ControlProps<T> & { ref?: Ref<HTMLDivElement | HTMLLabelElement> },
) => ReactElement;
